import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

// iter271 — fixed list so cancellation wording stays consistent for reporting.
const CANCEL_REASONS = [
  ["no_budget", "No budget"],
  ["lost_to_competition", "Lost to competition"],
  ["customer_cancelled", "Customer cancelled"],
  ["no_longer_required", "No longer required"],
  ["non_payment", "Non-payment"],
  ["other", "Other (describe below)"],
];
import { Eye, FileDown } from "lucide-react";
import { BTN } from "@/components/common/btn";
import { WODraftActions } from "./WODraftActions";
import { WOSubmittedActions } from "./WOSubmittedActions";
import { WOApprovedActions } from "./WOApprovedActions";
import { WOScheduledActions } from "./WOScheduledActions";
import { WORestoreButton } from "./WORestoreButton";
import PushJobSpButton from "@/components/integrations/PushJobSpButton";
import { useAuth } from "@/auth/AuthContext";

// Footer action row on the Work Order form. Renders three groups:
//   • Left:  Cancel-WO inline confirm + auto-save indicator.
//   • Right: View + PDF buttons (always when an existing WO is open).
//   • Right (status-specific): WODraftActions / WOSubmittedActions /
//     WOApprovedActions / WOScheduledActions / WORestoreButton
//     depending on form.status.
//   • Right (Foundation): push button visible once the WO is
//     scheduled/completed/invoiced/paid (iter164 ERP bridge).
// Extracted from WorkOrderTab.jsx (Feb 2026, iter180).
export function WOActionsRow({
  form, editingWO,
  confirmCancelWO, setConfirmCancelWO,
  woSaveStatus,
  setShowForm, resetForm,
  handleSave,
  setShowWOPreview,
  handleDownloadPDF,
  openEmailDialog,
  confirmWOComplete, setConfirmWOComplete,
  scheduledDateRef,
  setForm,
}) {
  const [cancelCode, setCancelCode] = useState("");
  const [cancelDetail, setCancelDetail] = useState("");
  const { can } = useAuth();
  const canPushFoundation = can("integrations.foundation_queue", "write");
  return (
    <div className="flex flex-wrap gap-2 justify-between pt-3 border-t" data-testid="wo-actions-row">
      <div className="flex gap-2">
        {!editingWO && (
          !confirmCancelWO ? (
            <Button size="sm" className={BTN.nav} onClick={() => setConfirmCancelWO(true)} data-testid="btn-cancel-new-wo">Cancel</Button>
          ) : (
            <div className="flex items-center gap-2 animate-in fade-in duration-200">
              <span className="text-xs font-medium text-red-700 bg-red-50 px-2 py-1 rounded">Are you sure?</span>
              <Button size="sm" className={BTN.danger} onClick={() => { setConfirmCancelWO(false); setShowForm(false); resetForm(); }} data-testid="btn-confirm-cancel-new-wo">Yes</Button>
              <Button size="sm" className={BTN.nav} onClick={() => setConfirmCancelWO(false)}>No</Button>
            </div>
          )
        )}
        {editingWO && form.status !== "completed" && form.status !== "cancelled" && (
          !confirmCancelWO ? (
            <Button size="sm" className={BTN.nav}
              onClick={() => setConfirmCancelWO(true)} data-testid="btn-cancel-wo">Cancel WO</Button>
          ) : (
            /* iter270 — a cancellation now needs a reason. It lands on the
               WO (banner + note history) and any not-yet-completed
               dispatches come off the schedule. */
            <div className="flex items-start gap-2 animate-in fade-in duration-200 flex-wrap max-w-2xl" data-testid="cancel-wo-form">
              <div className="text-xs font-medium text-red-700 bg-red-50 px-2 py-1 rounded w-full">
                Cancel this Work Order? It moves to history as cancelled, won't count as won revenue,
                and any scheduled visits are removed from the technicians' schedules.
              </div>
              <Select value={cancelCode} onValueChange={setCancelCode}>
                <SelectTrigger className="h-8 text-xs w-[210px]" data-testid="select-cancel-reason">
                  <SelectValue placeholder="Reason (required)" />
                </SelectTrigger>
                <SelectContent>
                  {CANCEL_REASONS.map(([v, l]) => <SelectItem key={v} value={v}>{l}</SelectItem>)}
                </SelectContent>
              </Select>
              <Input
                value={cancelDetail}
                onChange={(e) => setCancelDetail(e.target.value)}
                placeholder={cancelCode === "other" ? "Describe the reason (required)" : "Extra detail (optional)"}
                className="h-8 text-xs flex-1 min-w-[200px]"
                data-testid="input-cancel-reason"
              />
              <Button
                size="sm"
                className={`${BTN.danger} disabled:opacity-40`}
                disabled={!cancelCode || (cancelCode === "other" && !cancelDetail.trim())}
                onClick={() => {
                  setConfirmCancelWO(false);
                  handleSave("cancelled", { code: cancelCode, detail: cancelDetail.trim() });
                  setCancelCode(""); setCancelDetail("");
                }}
                data-testid="btn-confirm-cancel-wo"
              >
                Yes, cancel
              </Button>
              <Button size="sm" className={BTN.nav} onClick={() => { setConfirmCancelWO(false); setCancelCode(""); setCancelDetail(""); }}>No</Button>
            </div>
          )
        )}
        {woSaveStatus && <span className="text-xs text-slate-400 italic self-center" data-testid="wo-save-indicator">{woSaveStatus}</span>}
      </div>

      <div className="flex flex-wrap gap-2 justify-end">
        {editingWO && (
          <>
            <Button size="sm" className={BTN.nav} onClick={() => setShowWOPreview(true)} title="View" data-testid="btn-view-wo">
              <Eye className="w-3.5 h-3.5" />
            </Button>
            <Button size="sm" className={BTN.nav} onClick={handleDownloadPDF} data-testid="btn-download-pdf">
              <FileDown className="w-3.5 h-3.5 mr-1" /> PDF
            </Button>
          </>
        )}
        {form.status === "draft" && (
          <WODraftActions
            editingWO={editingWO}
            handleSave={handleSave}
            openEmailDialog={openEmailDialog}
            confirmWOComplete={confirmWOComplete}
            setConfirmWOComplete={setConfirmWOComplete}
          />
        )}
        {form.status === "submitted" && editingWO && (
          <WOSubmittedActions
            handleSave={handleSave}
            openEmailDialog={openEmailDialog}
            confirmWOComplete={confirmWOComplete}
            setConfirmWOComplete={setConfirmWOComplete}
          />
        )}
        {(form.status === "approved" || form.status === "on_hand") && editingWO && (
          <WOApprovedActions
            form={form}
            editingWO={editingWO}
            handleSave={handleSave}
            openEmailDialog={openEmailDialog}
            confirmWOComplete={confirmWOComplete}
            setConfirmWOComplete={setConfirmWOComplete}
            scheduledDateRef={scheduledDateRef}
          />
        )}
        {form.status === "scheduled" && editingWO && (
          <WOScheduledActions
            handleSave={handleSave}
            openEmailDialog={openEmailDialog}
            confirmWOComplete={confirmWOComplete}
            setConfirmWOComplete={setConfirmWOComplete}
          />
        )}
        {form.status === "cancelled" && editingWO && (
          <WORestoreButton variant="cancelled" onClick={() => handleSave("draft")} />
        )}
        {form.status === "completed" && editingWO && (
          <WORestoreButton variant="completed" onClick={() => handleSave("draft")} />
        )}
        {/* iter164 — Foundation push button, visible once the WO
            is scheduled/completed/invoiced (we don't push drafts).
            Shows current push status as a chip when already
            queued/sent; allows re-push on `failed`/`rejected`.
            Uses `editingWO.id` rather than `form.id` because
            the form state shape doesn't carry the WO id. */}
        {/* Foundation Phase 1 — sp_insert_update_jobs. Admin-only; the
            direct dbo.jobs INSERT path is parked. */}
        {canPushFoundation && editingWO?.id && ["scheduled", "completed", "invoiced", "paid"].includes(form.status) && (
          <PushJobSpButton
            woId={editingWO.id}
            lastStatus={form.foundation_sp_push_status || editingWO?.foundation_sp_push_status || null}
            onPushed={(res) => {
              setForm((f) => ({
                ...f,
                foundation_sp_push_log_id: res.log_id,
                foundation_sp_push_status: res.outcome,
              }));
            }}
          />
        )}
      </div>
    </div>
  );
}
